// Settings -> Material & Service Management: the ONE Tenant Admin screen for everything
// pricing-related — the tenant's own supplier/price catalogue (formerly its own "Inventory" tab,
// folded in here so materials, services and their negotiated pricing all live in one place),
// preferred supplier/manufacturer per material, and the tenant's own labour rates.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MaterialsPreferenceSection from '@/components/Settings/MaterialsPreferenceSection';
import ServiceRatesSection from '@/components/Settings/ServiceRatesSection';
import InventorySection from '@/components/Settings/InventorySection';
import { Tabs } from '@/components/UI/Tabs';
import { cn } from '@/utils/cn';


type Toast = { type: 'success' | 'error'; msg: string };
type InnerTab = 'materials' | 'services' | 'inventory';

const MaterialServiceManagementSection: React.FC = () => {
  const { t } = useTranslation();
  const [innerTab, setInnerTab] = useState<InnerTab>('materials');
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = (next: Toast) => {
    setToast(next);
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <div className="space-y-5">
      {toast && (
        <div
          className={cn(
            'fixed top-4 right-4 z-toast rounded-control px-4 py-3 text-sm text-white shadow-pop',
            toast.type === 'success' ? 'bg-success-fill' : 'bg-danger-fill'
          )}
        >
          {toast.msg}
        </div>
      )}

      <div>
        <h2 className="text-base font-semibold text-ui-text">
          {t('settings.materialService.title', 'Material & Service Management')}
        </h2>
        <p className="mt-1 text-sm text-ui-text2">
          {t(
            'settings.materialService.description',
            'Choose which supplier and manufacturer estimates should prefer, and set your own labour rates.'
          )}
        </p>
      </div>

      <Tabs
        tabs={[
          { id: 'materials', label: t('settings.materialService.tabs.materials', 'Materials') },
          { id: 'services', label: t('settings.materialService.tabs.services', 'Services') },
          { id: 'inventory', label: t('settings.materialService.tabs.inventory', 'Supplier Price List') },
        ]}
        value={innerTab}
        onChange={(id) => setInnerTab(id as InnerTab)}
        label={t('settings.materialService.title', 'Material & Service Management')}
      />

      {innerTab === 'materials' && <MaterialsPreferenceSection onToast={showToast} />}
      {innerTab === 'services' && <ServiceRatesSection onToast={showToast} />}
      {innerTab === 'inventory' && <InventorySection />}
    </div>
  );
};

export default MaterialServiceManagementSection;
